/**
 * The frame every onboarding page shares: brand mark, gradient rail, step
 * counter, question-style heading and a short hint. Used by the first-time
 * wizard (Redirect.tsx) and by the returning-member "Welcome back" prompts,
 * so the two read as one product.
 */
import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import WhiteLogo from '../../../assets/images/brand/dosen/white-logo.svg';
import { onboardingColors as C } from './onboardingTheme';
import { MOBILE_SX } from '../../../mobile/mobileApp';

interface OnboardingHeadingProps {
  heading?: React.ReactNode;
  hint?: React.ReactNode;
  /** Horizontal padding, in theme spacing units. Defaults to the card gutter. */
  px?: number;
  mt?: number;
}

/** Heading + hint block on its own, for pages that own their heading (the
 *  assessment renders one question per screen). */
export const OnboardingHeading: React.FC<OnboardingHeadingProps> = ({ heading, hint, px = 3, mt = 1.5 }) => {
  if (!heading && !hint) return null;
  return (
    // Phone: the app's 16px gutter and a 22px heading, so the step's fields get more of the screen.
    <Box px={px} mt={mt} sx={{ [MOBILE_SX]: { px: 2, mt: 1 } }}>
      {heading && (
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            color: C.ink,
            [MOBILE_SX]: { fontSize: 22 }
          }}
        >
          {heading}
        </Typography>
      )}
      {hint && (
        <Typography sx={{ mt: 0.75, fontSize: 13, fontStyle: 'italic', color: C.inkSecondary, lineHeight: 1.45 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

interface OnboardingShellProps {
  /** 0 to 100. Ignored when `required` is set (the rail renders full). */
  progress?: number;
  /** Right-aligned label under the rail, e.g. "Step 2 of 7". */
  stepLabel?: React.ReactNode;
  /** Standalone required prompt: full rail plus "Required to continue". */
  required?: boolean;
  heading?: React.ReactNode;
  hint?: React.ReactNode;
  children?: React.ReactNode;
}

const clamp = (n: number) => (Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0);

const OnboardingShell: React.FC<OnboardingShellProps> = ({ progress = 0, stepLabel, required, heading, hint, children }) => (
  <>
    <Box px={3} pt={3} sx={{ [MOBILE_SX]: { px: 2, pt: 2 } }}>
      <Box display="flex" justifyContent="center" mb={2.5} sx={{ [MOBILE_SX]: { mb: 1.5 } }}>
        <Box component="img" src={WhiteLogo} alt="Dosen" sx={{ height: 22, width: 'auto', display: 'block' }} />
      </Box>
      <LinearProgress variant="determinate" value={required ? 100 : clamp(progress)} aria-label="Onboarding progress" />
      <Typography
        sx={{ textAlign: 'right', mt: 0.75, fontSize: 12, fontWeight: 500, color: C.inkTertiary, minHeight: 18 }}
      >
        {required ? 'Required to continue' : stepLabel}
      </Typography>
    </Box>
    <OnboardingHeading heading={heading} hint={hint} />
    {children}
  </>
);

export default OnboardingShell;
